import {getFile} from "./media";
import {$http} from "../helpers/http";
import {axios} from "../helpers/http_client";
import {getCitiesOfArea} from "./district";
import {isEmpty, str_replace} from "../components/helpers/helpers";
import {getCurrentTime} from "../components/helpers/dates";
import {AreaStatItems} from "../components/organisms/areas/show/helpers";
import {getCacheKey} from "./cache_key";

const cacheTable = 'areas';

/*
* *****************************  API  **********************
* */

/**
 * @param area_id {Number}
 *
 * @return Promise <Area>
 * */
export function getAreaById(area_id) {
    const path = '/v1/areas/' + area_id + '/get';
    const cacheKey = getCacheKey(cacheTable, 'lists', 'id', [area_id]);
    const cacheTTL = 6 * 60 * 60; // second

    return new Promise((resolve, reject) => {
        $http(cacheKey, path, cacheTTL).then(result => {
            const areaItem = result.area;
            const stats = result.stats;

            // set default stat value if area not stat
            for (let stat of AreaStatItems) {
                if (stats[stat + '_percent'] < 1) {
                    stats[stat] = 1;
                    stats[stat + '_percent'] = 50; // default
                }
            }

            if (stats['count'] < 1) {
                stats['count'] = 1;
            }

            if (stats['rate'] < 1) {
                stats['rate'] = 3;
            }

            areaItem['stats'] = result.stats;

            resolve(areaItem);
        }).catch(err => {
            reject(err);
        })
    })
}

/**
 * @param area_id {Number}
 *
 * @return Promise
 * */
export function getAroundAreas(area_id) {
    const path = '/v1/areas/' + area_id + '/around';
    const cacheKey = getCacheKey(cacheTable, 'around', 'byArea', [area_id]);
    const cacheTTL = 24 * 60 * 60; // second

    return new Promise((resolve, reject) => {
        $http(cacheKey, path, cacheTTL).then(result => {
            resolve(result);
        }).catch(err => {
            reject(err);
        })
    })
}

/**
 * @param district_id {Number}
 * @param type {String}
 *
 * @return Promise
 * */
export function getAreasByDistrictIdForAreaType(district_id, type) {
    const path = `/v1/areas/district/${district_id}/${type}`;
    const cacheKey = getCacheKey(cacheTable, 'areasByDistrictIdForAreaType', 'district-type', [district_id, type]);
    const cacheTTL = 24 * 60 * 60; // second

    return new Promise((resolve, reject) => {
        $http(cacheKey, path, cacheTTL).then(result => {
            resolve(result);
        }).catch(err => {
            reject(err);
        })
    })
}

/**
 * @param area_id {Number}
 * @param page {Number}
 *
 * @return Promise
 * */
export function getAreaComments(area_id, page = 1) {
    const path = '/v1/areas/' + area_id + '/comments';
    const cacheKey = getCacheKey(cacheTable, 'comments', 'area', [area_id]);
    const cacheTTL = 10 * 60; // second

    return new Promise((resolve, reject) => {
        $http(cacheKey, path, cacheTTL).then(result => {
            resolve(result);
        }).catch(err => {
            reject(err);
        })
    })
}

/**
 * @param area_id {Number}
 * @param data {Object}
 * data => {author_name = "", description = null, positives = null, negatives = null, transportation = null, culture = null, traffic = null, amenities = null, rate = null, lived_in = Boolean}
 *
 * @return Promise
 * */
export function storeAreaComment(area_id, data) {
    const path = '/v1/areas/' + area_id + '/comments';

    const storeData = {
        author_name: !isEmpty(data.author_name) ? data.author_name : 'کاربر مهمان',
        description: data.description,
        positives: data.positives,
        negatives: data.negatives,
        transportation: data.transportation,
        culture: data.culture,
        traffic: data.traffic,
        amenities: data.amenities,
        rate: data.rate,
        lived_in: data.lived_in,
        captcha_code: data.captcha,
    };

    return new Promise((resolve, reject) => {
        axios.post(path, storeData).then(result => {
            resolve(result.data);
        }).catch(err => {
            reject(err);
        })
    })
}

/**
 * @param area_id {number}
 * @param comment_id {number}
 *@param data {Object}
 *  data => {"author_name": "", "description": null}
 *
 * @return Promise
 * */
export function storeReplyToComment(area_id, comment_id, data) {
    const path = `/v1/areas/${area_id}/comments/${comment_id}/answer`;

    const storeData = {
        author_name: !isEmpty(data.author_name) ? data.author_name : 'کاربر مهمان',
        description: data.description,
        captcha_code: data.captcha,
    };

    return new Promise((resolve, reject) => {
        axios.post(path, storeData).then(result => {
            resolve(result.data);
        }).catch(err => {
            reject(err);
        })
    })
}

/**
 * @param area_id {Number}
 * @param comment_id {Number}
 * @param like {Boolean}
 *
 * @return Promise
 * */
export function storeAreaCommentLike(area_id, comment_id, like = true) {
    const path = `/v1/areas/${area_id}/comments/${comment_id}/` + (like ? 'like' : 'dislike');

    return new Promise((resolve, reject) => {
        axios.post(path).then(result => {
            resolve(result.data);
        }).catch(err => {
            reject(err);
        })
    })
}

/**
 * @param area_id {Number}
 *
 * @return Promise
 * */
export function getSelectedAreaComments(area_id) {
    const path = `/v1/areas/${area_id}/comments/selected`;
    const cacheKey = getCacheKey(cacheTable, 'selectedComments', 'area', [area_id]);
    const cacheTTL = 6 * 60 * 60; // second

    return new Promise((resolve, reject) => {
        $http(cacheKey, path, cacheTTL).then(result => {
            resolve(result);
        }).catch(err => {
            reject(err);
        })
    })
}


/*
* *****************************  Helpers  **********************
* */

/**
 * @param area_id {Number}
 * @param updated_at {Number}
 *
 * @return String
 * */
export function getAreaCover(area_id, updated_at = '') {
    return getFile('/areas/' + area_id + '/cover.jpg?' + updated_at);
}

/**
 * @param area_id {Number}
 * @param updated_at {Number}
 *
 * @return String
 * */
export function getAreaCoverThumb(area_id, updated_at = '') {
    return getFile('/areas/' + area_id + '/cover-thumb.jpg?' + updated_at);
}

/**
 * @return Promise
 * */
export function getNorthCities() {
    const shomal_id = 3198; // shomal area id
    const localNorthCitiesKey = `cities_by_area_${shomal_id}`;

    return new Promise((resolve, reject) => {
        let localNorthCities = null;

        if (process.browser) {
            localNorthCities = localStorage.getItem(localNorthCitiesKey);
            localNorthCities = JSON.parse(localNorthCities);
        }

        if (!isEmpty(localNorthCities)) {
            resolve(localNorthCities.lists);
        } else {

            getCitiesOfArea(shomal_id).then(result => {
                if (process.browser) {
                    const storeData = {
                        lists: result.districts,
                        expire: getCurrentTime(24 * 60 * 60) // 1 day
                    };

                    localStorage.setItem(localNorthCitiesKey, JSON.stringify(storeData));
                }

                resolve(result.districts);
            })
        }
    })
}

/**
 * @return Promise
 * */
export async function getNorthCitiesIds() {
    let ids = [3198, 17878, 17879]; // default ids => shomal & gilan & mazandaran

    const cities = await getNorthCities();

    if (!isEmpty(cities)) {
        Object.keys(cities).forEach(key => {
            const city = cities[key];
            ids.push(city.id)
        })
    }

    return ids;
}

/**
 * @param area
 *
 * @return String
 * */
export function getAreaUrl(area) {
    const city_name = !isEmpty(area.city_name) ? area.city_name.trim() : '';
    const district_name = !isEmpty(area.district_name) ? area.district_name.trim() : '';

    return '/محله/' + str_replace(' ', '-', city_name) + '/' + str_replace(' ', '-', district_name)
}